/**
 * Company Modal Component
 * Add/Edit company with data source configuration
 */

import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  X,
  Plus,
  Database,
  Check,
  Upload,
  FileJson,
  AlertCircle,
  Code,
  Layers,
} from 'lucide-react';
import Modal from '../shared/Modal';
import SchemaViewerModal from './SchemaViewerModal';
import { api } from '../../services/api';

interface DataSource {
  id: string;
  name: string;
  type: 'mongodb' | 'postgresql' | 'mysql' | 'bigquery' | 'json_upload' | 'api' | 'getresponse' | 'betterstack';
  sourceType: 'database' | 'file_upload' | 'api';
  config: {
    uri?: string;
    host?: string;
    port?: number;
    database: string;
    collection?: string;
    table?: string;
    username?: string;
    password?: string;
    uploadedFiles?: Array<{
      fileName: string;
      uploadDate: Date;
      recordCount: number;
    }>;
    apiEndpoint?: string;
    apiKey?: string;
    getresponseApiKey?: string;
    getresponseEndpoint?: string;
    betterstackApiKey?: string;
    betterstackEndpoint?: string;
    betterstackQueryEndpoint?: string;
    betterstackQueryUsername?: string;
    betterstackQueryPassword?: string;
    betterstackSourceId?: string;
  };
  availableModules: Array<
    'analytics' | 'funnel' | 'sessions' | 'dashboard' | 'email_campaigns' | 'attribution' | 'eshop' | 'betterstack'
  >;
  purpose?: string;
  isDefault: boolean;
  isActive: boolean;
  pendingFiles?: File[]; // Files waiting to be uploaded when clicking "Update"
  // Schema introspection
  schema?: any;
  schemaLastUpdated?: Date;
  schemaHistory?: Array<{
    version: number;
    schema: any;
    pulledAt: Date;
    changes?: string;
  }>;
  selectedSchemaVersion?: number; // Which version to use for calculations (null = latest)
}

interface Company {
  _id: string;
  name: string;
  slug: string;
  domain?: string;
  subscription: {
    plan: string;
    maxUsers: number;
    maxProjects: number;
  };
  dataSources: DataSource[];
}

interface CompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  company?: Company;
}

const MODULE_OPTIONS = [
  { value: 'analytics', label: 'Analytics', description: 'General analytics and metrics' },
  { value: 'funnel', label: 'Funnel Analysis', description: 'User journey and conversion funnels' },
  { value: 'sessions', label: 'Sessions', description: 'Session tracking and analysis' },
  { value: 'dashboard', label: 'Dashboard', description: 'Overview and summary metrics' },
  { value: 'email_campaigns', label: 'Email Campaigns', description: 'Email campaign tracking' },
  {
    value: 'attribution',
    label: 'Attribution & ROAS',
    description: 'Marketing attribution and ROAS forecasting',
  },
  {
    value: 'eshop',
    label: 'E-shop Integration',
    description: 'E-commerce database integration for customers, orders, and abandoned carts',
  },
  {
    value: 'betterstack',
    label: 'BetterStack',
    description: 'BetterStack logs integration for monitoring and log management',
  },
];

const CompanyModal = ({ isOpen, onClose, company }: CompanyModalProps) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDataSourceForm, setShowDataSourceForm] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [pullingSchema, setPullingSchema] = useState<string | null>(null); // Track which DS is pulling
  const [schemaPreview, setSchemaPreview] = useState<{ [key: string]: any }>({}); // Store schemas by dataSourceId
  const [schemaViewerOpen, setSchemaViewerOpen] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState<{
    schema: any;
    dataSourceName: string;
    history?: any[];
  } | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    domain: '',
    subscription: {
      plan: 'free',
      maxUsers: 5,
      maxProjects: 3,
    },
    dataSources: [] as DataSource[],
  });

  const [dataSourceForm, setDataSourceForm] = useState<Partial<DataSource>>({
    name: '',
    type: 'mongodb',
    sourceType: 'database',
    config: {
      database: '',
      collection: 'marketing',
    },
    availableModules: ['analytics', 'funnel', 'sessions', 'dashboard'],
    purpose: '',
    isDefault: false,
  });

  // State for JSON file upload
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [jsonDataPreview, setJsonDataPreview] = useState<any[]>([]);

  // Fetch available collections when URI is provided
  const [fetchingCollections, setFetchingCollections] = useState(false);
  const [availableCollections, setAvailableCollections] = useState<string[]>([]);
  const [loadingBetterStackSources, setLoadingBetterStackSources] = useState(false);
  const [betterStackSources, setBetterStackSources] = useState<Array<{ id: string; name: string; tableName: string; recommendedValue: string }>>([]);
  const [showBetterStackSourceDropdown, setShowBetterStackSourceDropdown] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        slug: company.slug,
        domain: company.domain || '',
        subscription: company.subscription,
        dataSources: (company.dataSources || []).map(ds => ({
          ...ds,
          // Ensure config exists with proper structure - preserve ALL fields including BetterStack Query API fields
          config: {
            // Start with defaults
            database: 'marketing',
            collection: 'imported_data',
            // For BetterStack, ensure defaults are set BEFORE spreading ds.config
            ...(ds.type === 'betterstack' ? {
              betterstackApiKey: '',
              betterstackEndpoint: 'https://telemetry.betterstack.com/api/v1',
              betterstackQueryEndpoint: '',
              betterstackQueryUsername: '',
              betterstackQueryPassword: '',
              betterstackSourceId: '',
            } : {}),
            // Then spread ALL existing config fields (this will override defaults with actual values from database)
            ...(ds.config || {}),
          },
          // Ensure new fields have defaults for existing data sources
          sourceType:
            ds.sourceType ||
            (ds.type === 'json_upload' ? 'file_upload' : ds.type === 'api' || ds.type === 'getresponse' || ds.type === 'betterstack' ? 'api' : 'database'),
          availableModules: ds.availableModules || ['analytics', 'funnel', 'sessions', 'dashboard'],
          purpose: ds.purpose || '',
          // Include schema fields
          schema: ds.schema,
          schemaLastUpdated: ds.schemaLastUpdated,
          schemaHistory: ds.schemaHistory,
          selectedSchemaVersion: ds.selectedSchemaVersion,
        })),
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        domain: '',
        subscription: {
          plan: 'free',
          maxUsers: 5,
          maxProjects: 3,
        },
        dataSources: [],
      });
    }
    // Clear BetterStack sources when switching companies or closing modal
    setBetterStackSources([]);
    setShowBetterStackSourceDropdown(false);
  }, [company, isOpen]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      // Save company first (without data sources)
      const { dataSources, ...companyData } = data;
      let savedCompany;

      if (company) {
        const response = await api.put(`/api/v1/companies/${company._id}`, companyData);
        savedCompany = response.data.data;
      } else {
        const response = await api.post('/api/v1/companies', companyData);
        savedCompany = response.data.data;
      }

      // STEP 1: Delete removed data sources
      if (company?.dataSources) {
        const currentDSIds = dataSources.map(ds => ds.id);
        const deletedDataSources = company.dataSources.filter(ds => !currentDSIds.includes(ds.id));

        for (const deletedDS of deletedDataSources) {
          await api.delete(`/api/v1/companies/${savedCompany._id}/data-sources/${deletedDS.id}`);
          toast.success(`🗑️ Deleted: ${deletedDS.name}`);
        }
      }

      // STEP 2: Update or create data sources
      for (const dataSource of dataSources) {
        // Check if it already exists
        const existingDS = company?.dataSources?.find(ds => ds.id === dataSource.id);

        if (existingDS) {
          // Update existing data source - only send updatable fields
          // Sanitize config - remove undefined/null values and ensure it's a plain object
          const sanitizedConfig = JSON.parse(JSON.stringify(dataSource.config || {}));
          
          await api.put(`/api/v1/companies/${savedCompany._id}/data-sources/${dataSource.id}`, {
            name: dataSource.name,
            config: sanitizedConfig,
            isDefault: dataSource.isDefault,
            isActive: dataSource.isActive,
            availableModules: dataSource.availableModules || [
              'analytics',
              'funnel',
              'sessions',
              'dashboard',
            ],
            purpose: dataSource.purpose || '',
          });
        } else if (dataSource.id.startsWith('new_')) {
          // Add new data source - send all required fields
          // Sanitize config - remove undefined/null values and ensure it's a plain object
          const sanitizedConfig = JSON.parse(JSON.stringify(dataSource.config || {}));
          
          const createResponse = await api.post(
            `/api/v1/companies/${savedCompany._id}/data-sources`,
            {
              name: dataSource.name,
              type: dataSource.type,
              sourceType: dataSource.sourceType || 'database',
              config: sanitizedConfig,
              isDefault: dataSource.isDefault,
              availableModules: dataSource.availableModules || [
                'analytics',
                'funnel',
                'sessions',
                'dashboard',
              ],
              purpose: dataSource.purpose || '',
            }
          );

          // If this is a file_upload source with pending files, upload them now!
          if (
            dataSource.sourceType === 'file_upload' &&
            dataSource.pendingFiles &&
            dataSource.pendingFiles.length > 0
          ) {
            // The backend returns the created data source directly in createResponse.data.data
            const createdDataSource = createResponse.data.data;

            if (createdDataSource && createdDataSource.id) {
              try {
                toast('📤 Uploading JSON files to MongoDB...', { icon: '⏳' });

                for (const file of dataSource.pendingFiles) {
                  const text = await file.text();
                  const json = JSON.parse(text);
                  const data = Array.isArray(json) ? json : [json];

                  await api.post(
                    `/api/v1/companies/${savedCompany._id}/data-sources/${createdDataSource.id}/upload-json`,
                    {
                      jsonData: data,
                      fileName: file.name,
                    }
                  );
                }

                toast.success(
                  `✅ ${dataSource.pendingFiles.length} JSON file(s) uploaded to MongoDB!`
                );
              } catch (uploadError: any) {
                console.error('Upload error:', uploadError);
                toast.error(
                  `Upload failed: ${
                    uploadError.response?.data?.error?.message || uploadError.message
                  }`
                );
              }
            } else {
              console.error('Created data source ID not found in response:', createResponse.data);
              toast.error('Failed to get created data source ID');
            }
          }
        }
      }

      return savedCompany;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success(company ? 'Company updated successfully' : 'Company created successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to save company');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Sanitize data sources - ensure port is a number
    const sanitizedFormData = {
      ...formData,
      dataSources: formData.dataSources.map(ds => ({
        ...ds,
        config: {
          ...ds.config,
          // Ensure port is a valid number, not a string or NaN
          ...(ds.config.port !== undefined && {
            port: (() => {
              const portValue = typeof ds.config.port === 'string' 
                ? parseInt(ds.config.port, 10) 
                : ds.config.port;
              // If port is NaN or invalid, use default based on DB type
              if (isNaN(portValue)) {
                return ds.type === 'postgresql' ? 5432 : ds.type === 'mysql' ? 3306 : 27017;
              }
              return portValue;
            })()
          })
        }
      }))
    };
    
    saveMutation.mutate(sanitizedFormData);
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    });
  };

  const fetchCollections = async () => {
    if (!dataSourceForm.config?.uri) {
      toast.error('Please enter a database URI first');
      return;
    }

    setFetchingCollections(true);
    try {
      const response = await api.post(`/api/v1/companies/${company?._id || 'temp'}/collections`, {
        uri: dataSourceForm.config.uri,
        database: dataSourceForm.config.database,
      });
      setAvailableCollections(response.data.data.collections || []);
      toast.success('Collections fetched successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to fetch collections');
      setAvailableCollections([]);
    } finally {
      setFetchingCollections(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);

    // Parse and preview JSON files
    const allJsonData: any[] = [];
    for (const file of files) {
      try {
        const text = await file.text();
        const json = JSON.parse(text);

        if (Array.isArray(json)) {
          allJsonData.push(...json);
        } else {
          allJsonData.push(json);
        }
      } catch (error) {
        toast.error(`Failed to parse ${file.name}: Invalid JSON`);
      }
    }

    setJsonDataPreview(allJsonData);
    toast.success(`${files.length} file(s) selected, ${allJsonData.length} records found`);
  };

  // Upload JSON files to the data source
  const uploadJsonFiles = async (dataSourceId: string) => {
    if (selectedFiles.length === 0) {
      toast.error('Please select JSON files first');
      return;
    }

    setUploadingFiles(true);
    try {
      for (const file of selectedFiles) {
        const text = await file.text();
        const json = JSON.parse(text);
        const data = Array.isArray(json) ? json : [json];

        await api.post(
          `/api/v1/companies/${company?._id}/data-sources/${dataSourceId}/upload-json`,
          {
            jsonData: data,
            fileName: file.name,
          }
        );
      }

      toast.success('All files uploaded successfully');
      setSelectedFiles([]);
      setJsonDataPreview([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to upload files');
    } finally {
      setUploadingFiles(false);
    }
  };

  const addDataSource = async () => {
    // Name validation
    if (!dataSourceForm.name) {
      toast.error('Please provide a data source name');
      return;
    }

    // Database-specific validation
    if (dataSourceForm.sourceType === 'database') {
      // For PostgreSQL/MySQL - check for URI OR individual credentials
      if (dataSourceForm.type === 'postgresql' || dataSourceForm.type === 'mysql') {
        const hasUri = dataSourceForm.config?.uri;
        const hasIndividualCreds =
          dataSourceForm.config?.host &&
          dataSourceForm.config?.database &&
          dataSourceForm.config?.username;

        if (!hasUri && !hasIndividualCreds) {
          toast.error('Please provide connection string OR individual database credentials');
          return;
        }
      }
      // For MongoDB - require database field
      else if (dataSourceForm.type === 'mongodb') {
        if (!dataSourceForm.config?.database) {
          toast.error('Please provide a database name');
          return;
        }
      }
    }

    // For file_upload, ensure collection is set and files are selected
    if (dataSourceForm.sourceType === 'file_upload') {
      if (!dataSourceForm.config?.collection) {
        toast.error('Please specify a collection name');
        return;
      }
      if (selectedFiles.length === 0) {
        toast.error('Please select at least one JSON file');
        return;
      }
    }

    // For API integrations
    if (dataSourceForm.sourceType === 'api') {
      if (dataSourceForm.type === 'getresponse') {
        if (!dataSourceForm.config?.getresponseApiKey) {
          toast.error('Please provide GetResponse API key');
          return;
        }
      } else if (dataSourceForm.type === 'betterstack') {
        if (!dataSourceForm.config?.betterstackApiKey) {
          toast.error('Please provide BetterStack API key');
          return;
        }
        // Query API credentials are optional - user can add them later
        // We'll just warn them that logs won't be queryable without Query API credentials
        if (!dataSourceForm.config?.betterstackQueryEndpoint || 
            !dataSourceForm.config?.betterstackQueryUsername || 
            !dataSourceForm.config?.betterstackQueryPassword) {
          toast.success('Data source saved. Note: Query API credentials are required to query logs. You can add them later in the data source settings.', { duration: 5000 });
          // Don't return - allow saving without Query API credentials
        }
      } else if (dataSourceForm.type === 'api') {
        if (!dataSourceForm.config?.apiEndpoint) {
          toast.error('Please provide API endpoint');
          return;
        }
      }
    }

    // Prepare config with defaults
    const config = { ...dataSourceForm.config } as any;

    // For PostgreSQL/MySQL, ensure database field has a value (extract from URI if needed)
    if (
      (dataSourceForm.type === 'postgresql' || dataSourceForm.type === 'mysql') &&
      !config.database
    ) {
      if (config.uri) {
        // Try to extract database name from URI (e.g., postgresql://user:pass@host:5432/dbname)
        const match = config.uri.match(/\/([^\/\?]+)(\?|$)/);
        if (match && match[1]) {
          config.database = match[1];
        } else {
          config.database = 'defaultdb'; // fallback
        }
      } else {
        config.database = 'defaultdb'; // fallback
      }
    }

    // Check if we're editing an existing data source (has an id that doesn't start with 'new_')
    const isEditing = dataSourceForm.id && !dataSourceForm.id.startsWith('new_');
    
    const updatedDataSource: DataSource = {
      id: isEditing ? dataSourceForm.id! : `new_${Date.now()}`,
      name: dataSourceForm.name!,
      type: dataSourceForm.type as any,
      sourceType: dataSourceForm.sourceType as any,
      config,
      isDefault: dataSourceForm.isDefault || (!isEditing && formData.dataSources.length === 0),
      isActive: isEditing ? (dataSourceForm.isActive !== undefined ? dataSourceForm.isActive : true) : true,
      availableModules: dataSourceForm.availableModules || [
        'analytics',
        'funnel',
        'sessions',
        'dashboard',
      ],
      purpose: dataSourceForm.purpose || '',
      pendingFiles: dataSourceForm.sourceType === 'file_upload' ? selectedFiles : undefined, // Store files!
      // Preserve schema fields when editing
      schema: isEditing ? dataSourceForm.schema : undefined,
      schemaLastUpdated: isEditing ? dataSourceForm.schemaLastUpdated : undefined,
      schemaHistory: isEditing ? dataSourceForm.schemaHistory : undefined,
      selectedSchemaVersion: isEditing ? dataSourceForm.selectedSchemaVersion : undefined,
    };

    if (isEditing) {
      // Update existing data source
      setFormData({
        ...formData,
        dataSources: formData.dataSources.map(ds => 
          ds.id === dataSourceForm.id ? updatedDataSource : ds
        ),
      });
      toast.success('Data source updated');
    } else {
      // Add new data source
      setFormData({
        ...formData,
        dataSources: [...formData.dataSources, updatedDataSource],
      });
      if (dataSourceForm.sourceType === 'file_upload') {
        toast.success(`Data source added with ${selectedFiles.length} file(s) ready to upload`);
      } else {
        toast.success('Data source added');
      }
    }

    // Reset form
    setDataSourceForm({
      name: '',
      type: 'mongodb',
      sourceType: 'database',
      config: { database: '', collection: 'marketing' },
      availableModules: ['analytics', 'funnel', 'sessions', 'dashboard'],
      purpose: '',
      isDefault: false,
    });
    setSelectedFiles([]); // Clear from form
    setJsonDataPreview([]);
    setShowDataSourceForm(false);
  };

  const removeDataSource = (id: string) => {
    setFormData({
      ...formData,
      dataSources: formData.dataSources.filter(ds => ds.id !== id),
    });
  };

  const setDefaultDataSource = (id: string) => {
    setFormData({
      ...formData,
      dataSources: formData.dataSources.map(ds => ({
        ...ds,
        isDefault: ds.id === id,
      })),
    });
  };

  const handleSourceTypeChange = (sourceType: 'database' | 'file_upload' | 'api') => {
    let defaultType: any = 'mongodb';
    let defaultConfig: any = { database: '', collection: 'marketing' };

    if (sourceType === 'file_upload') {
      defaultType = 'json_upload';
      defaultConfig = { database: 'marketing', collection: 'imported_data', uploadedFiles: [] };
    } else if (sourceType === 'api') {
      defaultType = 'api';
      defaultConfig = { database: 'marketing', apiEndpoint: '', apiKey: '' };
    }

    setDataSourceForm({
      ...dataSourceForm,
      sourceType,
      type: defaultType,
      config: defaultConfig,
    });
  };

  const toggleModule = (module: string) => {
    const currentModules = dataSourceForm.availableModules || [];
    const newModules = currentModules.includes(module as any)
      ? currentModules.filter(m => m !== module)
      : [...currentModules, module as any];

    setDataSourceForm({
      ...dataSourceForm,
      availableModules: newModules,
    });
  };

  /**
   * Pull real schema from database
   */
  const pullDataSourceSchema = async (dataSource: DataSource) => {
    if (!company) {
      toast.error('Save the company first before pulling schema');
      return;
    }

    if (dataSource.sourceType === 'file_upload' || dataSource.sourceType === 'api') {
      toast('Schema introspection only available for database sources');
      return;
    }

    setPullingSchema(dataSource.id);

    try {
      const response = await api.post(
        `/api/v1/companies/${company._id}/data-sources/${dataSource.id}/pull-schema`
      );

      const responseData = response.data.data;
      const schema = responseData;

      // Update local schema preview
      setSchemaPreview(prev => ({ ...prev, [dataSource.id]: schema }));

      // Update data source in formData with version info
      setFormData({
        ...formData,
        dataSources: formData.dataSources.map(ds =>
          ds.id === dataSource.id 
            ? { 
                ...ds, 
                schema, 
                schemaLastUpdated: new Date(),
                schemaHistory: responseData.schemaHistory || ds.schemaHistory || [],
              } 
            : ds
        ),
      });

      // 🔥 IMPORTANT: Invalidate React Query cache so schema persists when modal reopens
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      await queryClient.invalidateQueries({ queryKey: ['companies-selector'] });
      await queryClient.invalidateQueries({ queryKey: ['user-company', company._id] });

      // Refetch to update schema history in the modal
      await queryClient.refetchQueries({ queryKey: ['companies'] });

      const versionInfo = response.data.data.version 
        ? ` (v${response.data.data.version}/${response.data.data.totalVersions})`
        : '';
      
      toast.success(
        `✅ Schema pulled successfully! ${schema.tables?.length || schema.collections?.length} ${
          schema.tables ? 'tables' : 'collections'
        } found${versionInfo}${response.data.data.totalVersions > 1 ? ' - Select version below' : ''}`
      );
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to pull schema');
    } finally {
      setPullingSchema(null);
    }
  };

  /**
   * Load existing schema for a data source
   */
  const loadExistingSchema = async (dataSource: DataSource) => {
    if (!company) return;

    try {
      const response = await api.get(
        `/api/v1/companies/${company._id}/data-sources/${dataSource.id}/schema`
      );

      if (response.data.data.hasSchema) {
        setSchemaPreview(prev => ({ ...prev, [dataSource.id]: response.data.data.schema }));
        
        // Update formData with schema history and selected version
        setFormData(prevData => ({
          ...prevData,
          dataSources: prevData.dataSources.map(ds =>
            ds.id === dataSource.id
              ? {
                  ...ds,
                  schema: response.data.data.schema,
                  schemaHistory: response.data.data.schemaHistory || [],
                  selectedSchemaVersion: response.data.data.selectedSchemaVersion,
                }
              : ds
          ),
        }));
      }
    } catch (error) {
      // Silent fail - schema might not exist yet
      console.error('Error loading schema:', error);
    }
  };

  // Load schemas when modal opens or data sources change
  useEffect(() => {
    if (company && isOpen) {
      formData.dataSources.forEach(ds => {
        if (ds.schema) {
          setSchemaPreview(prev => ({ ...prev, [ds.id]: ds.schema }));
        } else {
          loadExistingSchema(ds);
        }
      });
    }
  }, [company, isOpen, formData.dataSources]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={company ? 'Edit Company' : 'New Company'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={e => handleNameChange(e.target.value)}
              className="input"
              placeholder="ACME Corporation"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Slug <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.slug}
              onChange={e => setFormData({ ...formData, slug: e.target.value })}
              className="input"
              placeholder="acme-corporation"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Domain</label>
          <input
            type="text"
            value={formData.domain}
            onChange={e => setFormData({ ...formData, domain: e.target.value })}
            className="input"
            placeholder="acme.com"
          />
        </div>

        {/* Subscription */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-4">Subscription</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Plan</label>
              <select
                value={formData.subscription.plan}
                onChange={e =>
                  setFormData({
                    ...formData,
                    subscription: { ...formData.subscription, plan: e.target.value },
                  })
                }
                className="input"
              >
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Users</label>
              <input
                type="number"
                min="1"
                value={formData.subscription.maxUsers}
                onChange={e =>
                  setFormData({
                    ...formData,
                    subscription: {
                      ...formData.subscription,
                      maxUsers: parseInt(e.target.value),
                    },
                  })
                }
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Projects</label>
              <input
                type="number"
                min="1"
                value={formData.subscription.maxProjects}
                onChange={e =>
                  setFormData({
                    ...formData,
                    subscription: {
                      ...formData.subscription,
                      maxProjects: parseInt(e.target.value),
                    },
                  })
                }
                className="input"
              />
            </div>
          </div>
        </div>

        {/* Data Sources */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Data Sources</h3>
              <p className="text-xs text-secondary-600 mt-1">
                💡 Pull real database schemas to enable adaptive neural calculations
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowDataSourceForm(!showDataSourceForm)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Data Source
            </button>
          </div>

          {/* Data Source List */}
          <div className="space-y-2 mb-4">
            {formData.dataSources.map(ds => (
              <div
                key={ds.id}
                className="bg-secondary-50 dark:bg-secondary-700 rounded-lg p-3 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Database className="w-5 h-5 text-primary-600 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{ds.name}</div>
                        {ds.isDefault && <span className="badge badge-primary">Default</span>}
                        <span className="badge badge-secondary">
                          {ds.sourceType === 'database'
                            ? '🗄️ Database'
                            : ds.sourceType === 'file_upload'
                            ? '📁 File Upload'
                            : '🔌 API'}
                        </span>
                      </div>
                      <div className="text-sm text-secondary-600 mt-1">
                        {ds.type} - {ds.config.database}
                        {ds.config.collection && ` / ${ds.config.collection}`}
                      </div>
                      {ds.purpose && (
                        <div className="text-sm text-secondary-500 mt-1 italic">{ds.purpose}</div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(ds.availableModules || []).map(module => (
                          <span
                            key={module}
                            className="text-xs px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded"
                          >
                            {MODULE_OPTIONS.find(m => m.value === module)?.label || module}
                          </span>
                        ))}
                      </div>
                      {/* Show uploaded files count */}
                      {ds.config.uploadedFiles && ds.config.uploadedFiles.length > 0 && (
                        <div className="text-xs text-secondary-500 mt-2">
                          <span>📎 {ds.config.uploadedFiles.length} file(s) uploaded</span>
                        </div>
                      )}

                      {/* Show pending files count */}
                      {ds.pendingFiles && ds.pendingFiles.length > 0 && (
                        <div className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                          <span>
                            ⏳ {ds.pendingFiles.length} file(s) will be uploaded when you click
                            "Update"
                          </span>
                        </div>
                      )}

                      <div className="mt-3 flex gap-2">
                        {/* Pull Real Schema Button */}
                        {ds.sourceType === 'database' && (
                          <button
                            type="button"
                            onClick={() => pullDataSourceSchema(ds)}
                            disabled={pullingSchema === ds.id || !company}
                            className="text-xs px-3 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-300 rounded hover:from-green-200 hover:to-emerald-200 dark:hover:from-green-900/50 dark:hover:to-emerald-900/50 transition-all flex items-center gap-1 disabled:opacity-50"
                            title="Pull real database schema and store it"
                          >
                            {pullingSchema === ds.id ? (
                              <>
                                <Layers className="w-3 h-3 animate-spin" />
                                Pulling...
                              </>
                            ) : (
                              <>
                                <Layers className="w-3 h-3" />
                                🔄 Pull Real Schema
                              </>
                            )}
                          </button>
                        )}

                        {/* Show schema status with version */}
                        {ds.schemaLastUpdated && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                                ✅ Schema: {new Date(ds.schemaLastUpdated).toLocaleDateString()}
                              </span>
                              {ds.schemaHistory && ds.schemaHistory.length > 0 && (
                                <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded text-xs font-semibold">
                                  {ds.schemaHistory.length} {ds.schemaHistory.length === 1 ? 'version' : 'versions'}
                                </span>
                              )}
                              {/* View Schema Button */}
                              {(ds.schema || schemaPreview[ds.id]) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedSchema({
                                      schema: ds.schema || schemaPreview[ds.id],
                                      dataSourceName: ds.name,
                                      history: ds.schemaHistory,
                                    });
                                    setSchemaViewerOpen(true);
                                  }}
                                  className="text-xs px-3 py-1.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white rounded flex items-center gap-1 transition"
                                  title="View detailed schema information"
                                >
                                  <Code className="w-3 h-3" />
                                  📊 View Schema
                                </button>
                              )}
                            </div>

                            {/* Schema Version Selector */}
                            {ds.schemaHistory && ds.schemaHistory.length > 1 && (
                              <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-secondary-700 dark:text-secondary-300">
                                  Active Version:
                                </label>
                                <select
                                  value={ds.selectedSchemaVersion || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const version = value === '' ? null : parseInt(value);
                                    
                                    // Update the data source
                                    setFormData({
                                      ...formData,
                                      dataSources: formData.dataSources.map(source =>
                                        source.id === ds.id
                                          ? { ...source, selectedSchemaVersion: version }
                                          : source
                                      ),
                                    });
                                  }}
                                  className="text-xs px-2 py-1 bg-white dark:bg-secondary-700 border border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                  <option value="">Latest (v{ds.schemaHistory.length})</option>
                                  {ds.schemaHistory.slice().reverse().map((historyEntry: any) => (
                                    <option key={historyEntry.version} value={historyEntry.version}>
                                      Version {historyEntry.version} - {new Date(historyEntry.pulledAt).toLocaleDateString()}
                                    </option>
                                  ))}
                                </select>
                                {ds.selectedSchemaVersion && (
                                  <span className="text-xs text-amber-600 dark:text-amber-400">
                                    ⚠️ Using older version
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Schema Preview Summary - Show info only, button is above */}
                      {schemaPreview[ds.id] && (
                        <div className="mt-3 p-3 bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 rounded border border-primary-200 dark:border-primary-800">
                          <div className="text-xs">
                            <span className="font-semibold text-primary-700 dark:text-primary-300">
                              📊 Schema: {schemaPreview[ds.id].type}
                            </span>
                            <span className="ml-2 text-secondary-600 dark:text-secondary-400">
                              {schemaPreview[ds.id].tables &&
                                `${schemaPreview[ds.id].tables.length} tables`}
                              {schemaPreview[ds.id].collections &&
                                `${schemaPreview[ds.id].collections.length} collections`}
                            </span>
                            {schemaPreview[ds.id].detectedPatterns && (
                              <span className="ml-2 text-green-600 dark:text-green-400">
                                • {Object.values(schemaPreview[ds.id].detectedPatterns).filter((v: any) => typeof v === 'boolean' && v).length} business patterns
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        // Load data source into form for editing - preserve ALL config fields
                        setDataSourceForm({
                          ...ds,
                          config: {
                            // Start with defaults
                            database: 'marketing',
                            collection: 'imported_data',
                            // For BetterStack, ensure defaults are set BEFORE spreading ds.config
                            ...(ds.type === 'betterstack' ? {
                              betterstackApiKey: '',
                              betterstackEndpoint: 'https://telemetry.betterstack.com/api/v1',
                              betterstackQueryEndpoint: '',
                              betterstackQueryUsername: '',
                              betterstackQueryPassword: '',
                              betterstackSourceId: '',
                            } : {}),
                            // Then spread ALL existing config fields (this will override defaults with actual values from database)
                            ...(ds.config || {}),
                          },
                        });
                        setShowDataSourceForm(true);
                        // Scroll to form
                        setTimeout(() => {
                          document.getElementById('data-source-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      className="btn btn-secondary text-sm"
                      title="Edit this data source"
                    >
                      Edit
                    </button>
                    {!ds.isDefault && (
                      <button
                        type="button"
                        onClick={() => setDefaultDataSource(ds.id)}
                        className="btn btn-secondary text-sm"
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeDataSource(ds.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Data Source Form */}
          {showDataSourceForm && (
            <div className="border border-primary-200 dark:border-primary-800 rounded-lg p-4 space-y-4 max-h-[600px] overflow-y-auto">
              {/* Source Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Source Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => handleSourceTypeChange('database')}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      dataSourceForm.sourceType === 'database'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-secondary-300 hover:border-primary-300'
                    }`}
                  >
                    <Database className="w-6 h-6 mx-auto mb-1" />
                    <div className="font-medium text-sm">Database</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSourceTypeChange('file_upload')}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      dataSourceForm.sourceType === 'file_upload'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-secondary-300 hover:border-primary-300'
                    }`}
                  >
                    <FileJson className="w-6 h-6 mx-auto mb-1" />
                    <div className="font-medium text-sm">JSON Upload</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSourceTypeChange('api')}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      dataSourceForm.sourceType === 'api'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-secondary-300 hover:border-primary-300'
                    }`}
                  >
                    <Upload className="w-6 h-6 mx-auto mb-1" />
                    <div className="font-medium text-sm">API</div>
                  </button>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Data Source Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={dataSourceForm.name}
                    onChange={e => setDataSourceForm({ ...dataSourceForm, name: e.target.value })}
                    className="input"
                    placeholder="Email Campaign Tracking"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Purpose</label>
                  <input
                    type="text"
                    value={dataSourceForm.purpose}
                    onChange={e =>
                      setDataSourceForm({ ...dataSourceForm, purpose: e.target.value })
                    }
                    className="input"
                    placeholder="Email campaign analytics data"
                  />
                </div>
              </div>

              {/* Module Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Available Modules <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {MODULE_OPTIONS.map(module => (
                    <div
                      key={module.value}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        dataSourceForm.availableModules?.includes(module.value as any)
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-secondary-300 hover:border-primary-300'
                      }`}
                      onClick={() => toggleModule(module.value)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{module.label}</div>
                          <div className="text-xs text-secondary-600">{module.description}</div>
                        </div>
                        {dataSourceForm.availableModules?.includes(module.value as any) && (
                          <Check className="w-4 h-4 text-primary-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Database Configuration */}
              {dataSourceForm.sourceType === 'database' && (
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-medium">Database Configuration</h4>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Database Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={dataSourceForm.type}
                      onChange={e =>
                        setDataSourceForm({ ...dataSourceForm, type: e.target.value as any })
                      }
                      className="input"
                    >
                      <option value="mongodb">MongoDB</option>
                      <option value="postgresql">PostgreSQL</option>
                      <option value="mysql">MySQL</option>
                      <option value="bigquery">BigQuery</option>
                    </select>
                  </div>

                  {/* PostgreSQL/MySQL Configuration */}
                  {(dataSourceForm.type === 'postgresql' || dataSourceForm.type === 'mysql') && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Connection String (DATABASE_URL) <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={dataSourceForm.config?.uri || ''}
                          onChange={e =>
                            setDataSourceForm({
                              ...dataSourceForm,
                              config: { ...dataSourceForm.config!, uri: e.target.value },
                            })
                          }
                          className="input font-mono text-sm"
                          rows={2}
                          placeholder={`${dataSourceForm.type}://username:password@host:${
                            dataSourceForm.type === 'postgresql' ? '5432' : '3306'
                          }/database${
                            dataSourceForm.type === 'postgresql' ? '?sslmode=require' : ''
                          }`}
                        />
                        <p className="text-xs text-secondary-500 mt-1">
                          Provide the full database connection string. You can also configure
                          individual credentials below.
                        </p>
                      </div>

                      <div className="border-t pt-4">
                        <p className="text-sm font-medium mb-3">OR use individual credentials:</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Host</label>
                            <input
                              type="text"
                              value={dataSourceForm.config?.host || ''}
                              onChange={e =>
                                setDataSourceForm({
                                  ...dataSourceForm,
                                  config: { ...dataSourceForm.config!, host: e.target.value },
                                })
                              }
                              className="input"
                              placeholder="localhost or db-host.example.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Port</label>
                            <input
                              type="number"
                              value={
                                dataSourceForm.config?.port ||
                                (dataSourceForm.type === 'postgresql' ? 5432 : 3306)
                              }
                              onChange={e =>
                                setDataSourceForm({
                                  ...dataSourceForm,
                                  config: {
                                    ...dataSourceForm.config!,
                                    port: parseInt(e.target.value),
                                  },
                                })
                              }
                              className="input"
                              placeholder={dataSourceForm.type === 'postgresql' ? '5432' : '3306'}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Database Name</label>
                            <input
                              type="text"
                              value={dataSourceForm.config?.database || ''}
                              onChange={e =>
                                setDataSourceForm({
                                  ...dataSourceForm,
                                  config: { ...dataSourceForm.config!, database: e.target.value },
                                })
                              }
                              className="input"
                              placeholder="defaultdb"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Username</label>
                            <input
                              type="text"
                              value={dataSourceForm.config?.username || ''}
                              onChange={e =>
                                setDataSourceForm({
                                  ...dataSourceForm,
                                  config: { ...dataSourceForm.config!, username: e.target.value },
                                })
                              }
                              className="input"
                              placeholder="admin"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">Password</label>
                            <input
                              type="password"
                              value={dataSourceForm.config?.password || ''}
                              onChange={e =>
                                setDataSourceForm({
                                  ...dataSourceForm,
                                  config: { ...dataSourceForm.config!, password: e.target.value },
                                })
                              }
                              className="input"
                              placeholder="••••••••"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {dataSourceForm.type === 'mongodb' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Connection URI <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={dataSourceForm.config?.uri || ''}
                          onChange={e =>
                            setDataSourceForm({
                              ...dataSourceForm,
                              config: { ...dataSourceForm.config!, uri: e.target.value },
                            })
                          }
                          className="input font-mono text-sm"
                          placeholder="mongodb://admin:password@host:27017/database?authSource=admin"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Database <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={dataSourceForm.config?.database || ''}
                            onChange={e =>
                              setDataSourceForm({
                                ...dataSourceForm,
                                config: { ...dataSourceForm.config!, database: e.target.value },
                              })
                            }
                            className="input"
                            placeholder="marketing"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                            Collection
                            <button
                              type="button"
                              onClick={fetchCollections}
                              disabled={fetchingCollections}
                              className="text-xs text-primary-600 hover:text-primary-700"
                            >
                              {fetchingCollections ? '...' : '(Fetch)'}
                            </button>
                          </label>
                          {availableCollections.length > 0 ? (
                            <select
                              value={dataSourceForm.config?.collection || ''}
                              onChange={e =>
                                setDataSourceForm({
                                  ...dataSourceForm,
                                  config: { ...dataSourceForm.config!, collection: e.target.value },
                                })
                              }
                              className="input"
                            >
                              <option value="">Select collection</option>
                              {availableCollections.map(col => (
                                <option key={col} value={col}>
                                  {col}
                                </option>
                              ))}
                              <option value="_create_new_">+ Create New Collection</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={dataSourceForm.config?.collection || ''}
                              onChange={e =>
                                setDataSourceForm({
                                  ...dataSourceForm,
                                  config: { ...dataSourceForm.config!, collection: e.target.value },
                                })
                              }
                              className="input"
                              placeholder="tracking_events"
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* JSON Upload Configuration */}
              {dataSourceForm.sourceType === 'file_upload' && (
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-medium">JSON File Upload</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Target Database <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={dataSourceForm.config?.database || 'marketing'}
                        onChange={e =>
                          setDataSourceForm({
                            ...dataSourceForm,
                            config: { ...dataSourceForm.config!, database: e.target.value },
                          })
                        }
                        className="input"
                        placeholder="marketing"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Collection Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={dataSourceForm.config?.collection || ''}
                        onChange={e =>
                          setDataSourceForm({
                            ...dataSourceForm,
                            config: { ...dataSourceForm.config!, collection: e.target.value },
                          })
                        }
                        className="input"
                        placeholder="email_campaigns"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Connection URI <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={dataSourceForm.config?.uri || ''}
                      onChange={e =>
                        setDataSourceForm({
                          ...dataSourceForm,
                          config: { ...dataSourceForm.config!, uri: e.target.value },
                        })
                      }
                      className="input font-mono text-sm"
                      placeholder="mongodb://admin:password@host:27017/marketing?authSource=admin"
                    />
                    <p className="text-xs text-secondary-500 mt-1">
                      This database connection will be used to upload the JSON files
                    </p>
                  </div>

                  <div className="border-2 border-dashed border-secondary-300 rounded-lg p-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      id="json-file-upload"
                    />
                    <label
                      htmlFor="json-file-upload"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <FileJson className="w-12 h-12 text-secondary-400 mb-2" />
                      <span className="text-sm font-medium">Click to select JSON files</span>
                      <span className="text-xs text-secondary-500 mt-1">
                        You can select multiple files at once
                      </span>
                    </label>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-primary-600" />
                        <span className="text-sm font-medium">Selected Files:</span>
                      </div>
                      <ul className="text-sm space-y-1">
                        {selectedFiles.map((file, idx) => (
                          <li key={idx} className="flex items-center justify-between">
                            <span>📄 {file.name}</span>
                            <span className="text-xs text-secondary-500">
                              {(file.size / 1024).toFixed(2)} KB
                            </span>
                          </li>
                        ))}
                      </ul>
                      {jsonDataPreview.length > 0 && (
                        <div className="mt-2 text-xs text-secondary-600">
                          Total records to upload: <strong>{jsonDataPreview.length}</strong>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* API Configuration */}
              {dataSourceForm.sourceType === 'api' && (
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-medium">API Configuration</h4>

                  {/* API Type Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      API Integration Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={dataSourceForm.type}
                      onChange={e => {
                        const newType = e.target.value as any;
                        let defaultConfig = { ...dataSourceForm.config!, database: 'marketing' };

                        if (newType === 'getresponse') {
                          defaultConfig.getresponseEndpoint = 'https://api.getresponse.com/v3';
                          defaultConfig.getresponseApiKey = '';
                        } else if (newType === 'betterstack') {
                          defaultConfig.betterstackEndpoint = 'https://telemetry.betterstack.com/api/v1';
                          defaultConfig.betterstackApiKey = '';
                          defaultConfig.betterstackQueryEndpoint = '';
                          defaultConfig.betterstackQueryUsername = '';
                          defaultConfig.betterstackQueryPassword = '';
                          defaultConfig.betterstackSourceId = '';
                        }

                        setDataSourceForm({
                          ...dataSourceForm,
                          type: newType,
                          config: defaultConfig,
                        });
                      }}
                      className="input"
                    >
                      <option value="api">Generic API</option>
                      <option value="getresponse">GetResponse</option>
                      <option value="betterstack">BetterStack</option>
                    </select>
                  </div>

                  {/* GetResponse Specific Configuration */}
                  {dataSourceForm.type === 'getresponse' && (
                    <>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                        <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                          GetResponse Integration
                        </p>
                        <p className="text-blue-700 dark:text-blue-300">
                          Get your API key from:{' '}
                          <a
                            href="https://app.getresponse.com/api"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            GetResponse API Settings
                          </a>
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          GetResponse API Key <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={dataSourceForm.config?.getresponseApiKey || ''}
                          onChange={e =>
                            setDataSourceForm({
                              ...dataSourceForm,
                              config: {
                                ...dataSourceForm.config!,
                                getresponseApiKey: e.target.value,
                              },
                            })
                          }
                          className="input font-mono text-sm"
                          placeholder="Your GetResponse API key"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">API Endpoint</label>
                        <select
                          value={
                            dataSourceForm.config?.getresponseEndpoint ||
                            'https://api.getresponse.com/v3'
                          }
                          onChange={e =>
                            setDataSourceForm({
                              ...dataSourceForm,
                              config: {
                                ...dataSourceForm.config!,
                                getresponseEndpoint: e.target.value,
                              },
                            })
                          }
                          className="input"
                        >
                          <option value="https://api.getresponse.com/v3">
                            Standard (api.getresponse.com)
                          </option>
                          <option value="https://api3.getresponse360.pl/v3">
                            GetResponse MAX (Poland)
                          </option>
                          <option value="https://api3.getresponse360.com/v3">
                            GetResponse MAX (Global)
                          </option>
                        </select>
                        <p className="text-xs text-secondary-500 mt-1">
                          Select GetResponse MAX if you're on the enterprise platform
                        </p>
                      </div>
                    </>
                  )}

                  {/* BetterStack Specific Configuration */}
                  {dataSourceForm.type === 'betterstack' && (
                    <>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                        <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                          BetterStack Integration
                        </p>
                        <p className="text-blue-700 dark:text-blue-300">
                          Get your API token from:{' '}
                          <a
                            href="https://betterstack.com/docs/logs/api/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            BetterStack API Settings
                          </a>
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          BetterStack API Key <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={dataSourceForm.config?.betterstackApiKey || ''}
                          onChange={e =>
                            setDataSourceForm({
                              ...dataSourceForm,
                              config: {
                                ...dataSourceForm.config!,
                                betterstackApiKey: e.target.value,
                              },
                            })
                          }
                          className="input font-mono text-sm"
                          placeholder="Your BetterStack API token"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Telemetry API Endpoint</label>
                        <input
                          type="text"
                          value={
                            dataSourceForm.config?.betterstackEndpoint ||
                            'https://telemetry.betterstack.com/api/v1'
                          }
                          onChange={e =>
                            setDataSourceForm({
                              ...dataSourceForm,
                              config: {
                                ...dataSourceForm.config!,
                                betterstackEndpoint: e.target.value,
                              },
                            })
                          }
                          className="input"
                          placeholder="https://telemetry.betterstack.com/api/v1"
                        />
                        <p className="text-xs text-secondary-500 mt-1">
                          BetterStack Telemetry API endpoint (for managing sources)
                        </p>
                      </div>

                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                        <h4 className="text-sm font-semibold mb-3 text-gray-900 dark:text-gray-100">
                          Query API Settings (Required for Log Querying)
                        </h4>
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm mb-3">
                          <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                            📋 How to get Query API credentials:
                          </p>
                          <ol className="list-decimal list-inside text-yellow-700 dark:text-yellow-300 space-y-1.5 text-xs">
                            <li>Log in to your <strong>BetterStack Dashboard</strong></li>
                            <li>Go to <strong>Dashboards</strong> in the left sidebar</li>
                            <li>Click <strong>"Connect remotely"</strong> button/link</li>
                            <li>Click <strong>"Connect ClickHouse HTTP client"</strong></li>
                            <li>
                              <strong>If you don't have a connection yet:</strong>
                              <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                                <li>Click <strong>"Create connection"</strong> or <strong>"New connection"</strong></li>
                                <li>Fill in the form (name, description, etc.)</li>
                                <li>Click <strong>"Create"</strong> or <strong>"Save"</strong></li>
                              </ul>
                            </li>
                            <li>
                              <strong>After creating (or if you already have one):</strong>
                              <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                                <li>You'll see a <strong>flash message/notification</strong> with your password</li>
                                <li><strong>⚠️ IMPORTANT:</strong> Copy the password immediately - it's only shown once!</li>
                                <li>You'll also see the <strong>endpoint URL</strong> (e.g., <code className="bg-yellow-100 dark:bg-yellow-900/30 px-1 rounded">https://eu-nbg-2-connect.betterstackdata.com</code>)</li>
                                <li>The <strong>username</strong> is usually displayed in the connection details</li>
                              </ul>
                            </li>
                            <li>Copy all three values and paste them into the fields below</li>
                          </ol>
                          <div className="mt-3 p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded text-xs">
                            <p className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">💡 Tip:</p>
                            <p className="text-yellow-800 dark:text-yellow-200">
                              If you can't find your password, you may need to create a new connection. The password is only displayed once when you first create the connection.
                            </p>
                          </div>
                          <div className="mt-3 p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-xs">
                            <p className="font-semibold text-blue-900 dark:text-blue-100 mb-1">📍 Can't find "Connect remotely"?</p>
                            <p className="text-blue-800 dark:text-blue-200 mb-2">
                              The "Connect remotely" option is located in the <strong>Dashboards</strong> section. Here's how to find it:
                            </p>
                            <ol className="list-decimal list-inside text-blue-700 dark:text-blue-300 space-y-1 ml-2">
                              <li>In BetterStack Dashboard, look for <strong>"Dashboards"</strong> in the left sidebar</li>
                              <li>Click on <strong>"Dashboards"</strong></li>
                              <li>Look for a button or link that says <strong>"Connect remotely"</strong> (it might be in the top right or in a menu)</li>
                              <li>If you still can't find it, try:
                                <ul className="list-disc list-inside ml-4 mt-1">
                                  <li>Settings → API tokens → Connections</li>
                                  <li>Settings → Integrations → Connections</li>
                                  <li>Or check if you're on a paid plan (this feature may require a paid subscription)</li>
                                </ul>
                              </li>
                            </ol>
                            <p className="text-blue-800 dark:text-blue-200 mt-2">
                              <strong>Alternative:</strong> You can also use BetterStack's <strong>Connections API</strong> to list existing connections programmatically. However, the password is only shown once when you create the connection, so if you've lost it, you'll need to create a new connection.
                            </p>
                            <p className="text-blue-800 dark:text-blue-200 mt-2">
                              <strong>Note:</strong> You can save this data source without Query API credentials and add them later. The Telemetry API token alone allows you to manage sources.
                            </p>
                          </div>
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                            <a
                              href="https://betterstack.com/docs/logs/query-api/v2/dashboards/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline font-medium"
                            >
                              📖 View full documentation →
                            </a>
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Query API Endpoint <span className="text-red-500">*</span> <span className="text-orange-500">(Required to query logs)</span>
                          </label>
                          <input
                            type="text"
                            value={dataSourceForm.config?.betterstackQueryEndpoint || ''}
                            onChange={e =>
                              setDataSourceForm({
                                ...dataSourceForm,
                                config: {
                                  ...dataSourceForm.config!,
                                  betterstackQueryEndpoint: e.target.value,
                                },
                              })
                            }
                            className="input"
                            placeholder="https://eu-nbg-2-connect.betterstackdata.com"
                          />
                          <p className="text-xs text-secondary-500 mt-1">
                            Your region-specific ClickHouse Query API endpoint. Without this, you can only manage sources (not query logs).
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Query API Username <span className="text-red-500">*</span> <span className="text-orange-500">(Required to query logs)</span>
                          </label>
                          <input
                            type="text"
                            value={dataSourceForm.config?.betterstackQueryUsername || ''}
                            onChange={e =>
                              setDataSourceForm({
                                ...dataSourceForm,
                                config: {
                                  ...dataSourceForm.config!,
                                  betterstackQueryUsername: e.target.value,
                                },
                              })
                            }
                            className="input"
                            placeholder="ClickHouse HTTP client username"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Query API Password <span className="text-red-500">*</span> <span className="text-orange-500">(Required to query logs)</span>
                          </label>
                          <input
                            type="password"
                            value={dataSourceForm.config?.betterstackQueryPassword || ''}
                            onChange={e =>
                              setDataSourceForm({
                                ...dataSourceForm,
                                config: {
                                  ...dataSourceForm.config!,
                                  betterstackQueryPassword: e.target.value,
                                },
                              })
                            }
                            className="input"
                            placeholder="ClickHouse HTTP client password"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            BetterStack Source ID / Table Name <span className="text-orange-500">(Optional - Recommended)</span>
                          </label>
                          <div className="flex gap-2">
                            <div className="flex-1 relative">
                              <input
                                type="text"
                                value={dataSourceForm.config?.betterstackSourceId || ''}
                                onChange={e =>
                                  setDataSourceForm({
                                    ...dataSourceForm,
                                    config: {
                                      ...dataSourceForm.config!,
                                      betterstackSourceId: e.target.value,
                                    },
                                  })
                                }
                                className="input w-full"
                                placeholder="e.g., t336915_mainapp or mainapp"
                                onFocus={() => {
                                  if (betterStackSources.length > 0) {
                                    setShowBetterStackSourceDropdown(true);
                                  }
                                }}
                                onBlur={() => {
                                  // Delay hiding dropdown to allow click events
                                  setTimeout(() => setShowBetterStackSourceDropdown(false), 200);
                                }}
                              />
                              {showBetterStackSourceDropdown && betterStackSources.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                                  {betterStackSources.map((source) => (
                                    <div
                                      key={source.id}
                                      className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                      onMouseDown={(e) => {
                                        e.preventDefault(); // Prevent input blur
                                        setDataSourceForm({
                                          ...dataSourceForm,
                                          config: {
                                            ...dataSourceForm.config!,
                                            betterstackSourceId: source.recommendedValue || source.tableName || source.name,
                                          },
                                        });
                                        setShowBetterStackSourceDropdown(false);
                                      }}
                                    >
                                      <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{source.name}</div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        Table: {source.tableName || source.recommendedValue || 'N/A'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!company?._id || !dataSourceForm.id) {
                                  toast.error('Please save the data source first before loading sources');
                                  return;
                                }
                                setLoadingBetterStackSources(true);
                                try {
                                  const response = await api.get(`/api/v1/betterstack/sources`, {
                                    params: {
                                      companyId: company._id,
                                      dataSourceId: dataSourceForm.id,
                                    },
                                  });
                                  if (response.data.success && response.data.data?.sources) {
                                    setBetterStackSources(response.data.data.sources);
                                    setShowBetterStackSourceDropdown(true);
                                    toast.success(`Loaded ${response.data.data.sources.length} source(s)`);
                                  } else {
                                    toast.error('Failed to load sources');
                                  }
                                } catch (error: any) {
                                  console.error('Failed to load BetterStack sources:', error);
                                  toast.error(error.response?.data?.message || 'Failed to load BetterStack sources. Make sure Telemetry API token is configured.');
                                } finally {
                                  setLoadingBetterStackSources(false);
                                }
                              }}
                              disabled={loadingBetterStackSources || !company?._id || !dataSourceForm.id}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                            >
                              {loadingBetterStackSources ? 'Loading...' : 'Load Sources'}
                            </button>
                          </div>
                          {betterStackSources.length > 0 && (
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                              💡 {betterStackSources.length} source(s) loaded. Click on a source in the dropdown to select it.
                            </p>
                          )}
                          <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs">
                            <p className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">📍 How to find your Source ID / Table Name:</p>
                            <p className="text-yellow-800 dark:text-yellow-200 mb-2">
                              <strong>✨ Easiest Method: Click "Load Sources" Button</strong>
                            </p>
                            <ol className="list-decimal list-inside space-y-1 text-yellow-800 dark:text-yellow-200 ml-2 mb-3">
                              <li>Make sure you've saved the data source with your Telemetry API token</li>
                              <li>Click the <strong>"Load Sources"</strong> button next to the input field</li>
                              <li>A dropdown will appear with all your BetterStack sources</li>
                              <li>Click on a source to automatically fill in the table name</li>
                            </ol>
                            <p className="text-yellow-800 dark:text-yellow-200 mb-2">
                              <strong>Method 2: From BetterStack Dashboard URL</strong>
                            </p>
                            <ol className="list-decimal list-inside space-y-1 text-yellow-800 dark:text-yellow-200 ml-2">
                              <li>In BetterStack Dashboard, go to your source's page (e.g., Live tail or Sources)</li>
                              <li>Look at the URL in your browser - it will show something like: <code className="bg-yellow-100 dark:bg-yellow-900/30 px-1 rounded">.../tail?s=1179475</code></li>
                              <li>The number after <code className="bg-yellow-100 dark:bg-yellow-900/30 px-1 rounded">s=</code> is your <strong>Source ID</strong> (e.g., <code className="bg-yellow-100 dark:bg-yellow-900/30 px-1 rounded">1179475</code>)</li>
                              <li>You can use this Source ID directly, OR use the source name (e.g., "MainApp")</li>
                            </ol>
                            <p className="text-yellow-800 dark:text-yellow-200 mt-2">
                              <strong>⚠️ Note:</strong> If you leave this empty, the system will try to query all sources, but this requires the Telemetry API to work. If you get a 404 error, you must configure this field.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Generic API Configuration */}
                  {dataSourceForm.type === 'api' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          API Endpoint <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={dataSourceForm.config?.apiEndpoint || ''}
                          onChange={e =>
                            setDataSourceForm({
                              ...dataSourceForm,
                              config: { ...dataSourceForm.config!, apiEndpoint: e.target.value },
                            })
                          }
                          className="input"
                          placeholder="https://api.example.com/data"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">API Key</label>
                        <input
                          type="password"
                          value={dataSourceForm.config?.apiKey || ''}
                          onChange={e =>
                            setDataSourceForm({
                              ...dataSourceForm,
                              config: { ...dataSourceForm.config!, apiKey: e.target.value },
                            })
                          }
                          className="input"
                          placeholder="Your API key"
                        />
                      </div>
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Database Name</label>
                      <input
                        type="text"
                        value={dataSourceForm.config?.database || 'marketing'}
                        onChange={e =>
                          setDataSourceForm({
                            ...dataSourceForm,
                            config: { ...dataSourceForm.config!, database: e.target.value },
                          })
                        }
                        className="input"
                        placeholder="marketing"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Collection</label>
                      <input
                        type="text"
                        value={dataSourceForm.config?.collection || ''}
                        onChange={e =>
                          setDataSourceForm({
                            ...dataSourceForm,
                            config: { ...dataSourceForm.config!, collection: e.target.value },
                          })
                        }
                        className="input"
                        placeholder="api_data"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Default Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={dataSourceForm.isDefault || formData.dataSources.length === 0}
                  onChange={e =>
                    setDataSourceForm({ ...dataSourceForm, isDefault: e.target.checked })
                  }
                  className="w-4 h-4"
                />
                <label htmlFor="isDefault" className="text-sm">
                  Set as default data source
                </label>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowDataSourceForm(false);
                    setSelectedFiles([]);
                    setJsonDataPreview([]);
                    // Reset form when canceling
                    setDataSourceForm({
                      name: '',
                      type: 'mongodb',
                      sourceType: 'database',
                      config: {
                        database: '',
                        collection: 'marketing',
                      },
                      availableModules: ['analytics', 'funnel', 'sessions', 'dashboard'],
                      purpose: '',
                      isDefault: false,
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="button" onClick={addDataSource} className="btn btn-primary">
                  <Check className="w-4 h-4 mr-2" />
                  {dataSourceForm.id && !dataSourceForm.id.startsWith('new_') ? 'Update Data Source' : 'Add Data Source'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={saveMutation.isPending} className="btn btn-primary">
            {saveMutation.isPending ? 'Saving...' : company ? 'Update' : 'Create'}
          </button>
        </div>
      </form>

      {/* Schema Viewer Modal */}
      {selectedSchema && (
        <SchemaViewerModal
          isOpen={schemaViewerOpen}
          onClose={() => {
            setSchemaViewerOpen(false);
            setSelectedSchema(null);
          }}
          schema={selectedSchema.schema}
          dataSourceName={selectedSchema.dataSourceName}
          schemaHistory={selectedSchema.history}
        />
      )}
    </Modal>
  );
};

export default CompanyModal;
